// =============================================================================
// TabsContext — open-screen tabs, driven by the URL
// =============================================================================
// Holds the list of open tabs, which one is active, and the per-tab refresh
// tick. The URL stays the source of truth: navigating to a screen (sidebar
// NavLink, deep link, browser back/forward) opens or focuses its tab, and
// closing a tab navigates to the neighbour it falls back to.
//
// Mirrors the SWAS ERP employee shell: activating a tab always bumps its
// refresh tick (screens can opt in with useTabRefresh to re-pull their data
// in place, without remounting and losing what the user had on screen).
// =============================================================================

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HOME_PATH, findScreen } from "../layouts/screens";

const TabsContext = createContext(null);

export const useTabs = () => useContext(TabsContext);

export function TabsProvider({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const screen = findScreen(pathname);

  /* Every screen gets a tab, the Dashboard included.
   *
   * It used to be excluded as "home": it rendered underneath the strip when no
   * tab was active, so the one screen people return to most was the one they
   * could not get back to by clicking. Going to it from a sidebar link also
   * blanked the strip, which reads as the tabs having been lost rather than as
   * one of them being unselected.
   *
   * It is still HOME_PATH — closing the last tab falls back to it — but that is
   * now a fallback destination, not a reason to treat it differently once you
   * are there. */
  const activeKey = screen ? screen.path : null;

  const [tabs, setTabs] = useState([]);
  const [reloadTicks, setReloadTicks] = useState({});

  const bumpReload = useCallback((key) => {
    setReloadTicks((m) => ({ ...m, [key]: (m[key] || 0) + 1 }));
  }, []);

  // Landing on a screen opens its tab (or focuses the existing one) and asks it
  // for fresh data.
  useEffect(() => {
    if (!activeKey) return;
    const label = findScreen(activeKey)?.label ?? activeKey;
    setTabs((prev) =>
      prev.some((t) => t.key === activeKey) ? prev : [...prev, { key: activeKey, label }]
    );
    bumpReload(activeKey);
  }, [activeKey, bumpReload]);

  // Clicking a tab navigates to it. Clicking the ALREADY-active tab re-pulls its
  // data (navigate() to the current path is a no-op, so bump it directly).
  const activate = useCallback(
    (key) => {
      if (key === activeKey) bumpReload(key);
      else navigate(key);
    },
    [activeKey, bumpReload, navigate]
  );

  /* Close a tab; if it was the active one, fall back to its neighbour (or home).
   *
   * The last remaining tab cannot be closed. Closing it would navigate to
   * HOME_PATH, which opens the Dashboard tab — so the button would either do
   * nothing visible or swap one lone tab for another. TabBar hides the ✕ in
   * that case; this is the same rule at the other end, for the context menu
   * and anything else that calls in. */
  const closeTab = useCallback(
    (key) => {
      if (tabs.length <= 1) return;
      const idx = tabs.findIndex((t) => t.key === key);
      if (idx < 0) return;
      const next = tabs.filter((t) => t.key !== key);
      setTabs(next);
      if (key === activeKey) {
        const neighbour = next[idx] || next[idx - 1] || null;
        navigate(neighbour ? neighbour.key : HOME_PATH);
      }
    },
    [tabs, activeKey, navigate]
  );

  // Close every tab EXCEPT the one clicked (which becomes active).
  const closeOthers = useCallback(
    (key) => {
      const keep = tabs.find((t) => t.key === key) || null;
      setTabs(keep ? [keep] : []);
      if (keep && key !== activeKey) navigate(keep.key);
      else if (!keep) navigate(HOME_PATH);
    },
    [tabs, activeKey, navigate]
  );

  /* Close everything and go back to the Dashboard — which then opens as the
     one remaining tab, because it is a screen like any other now. */
  const closeAll = useCallback(() => {
    setTabs([]);
    navigate(HOME_PATH);
  }, [navigate]);

  // Drag-to-reorder: move the dragged tab to the target INDEX chosen by the
  // drag. Committed once, on drop. The active tab is unchanged — it just rides
  // along to its new position.
  const reorder = useCallback((fromKey, toIndex) => {
    setTabs((prev) => {
      const from = prev.findIndex((t) => t.key === fromKey);
      if (from < 0) return prev;
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      const to = Math.max(0, Math.min(next.length, toIndex));
      if (to === from) return prev;
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ tabs, activeKey, reloadTicks, activate, closeTab, closeOthers, closeAll, reorder }),
    [tabs, activeKey, reloadTicks, activate, closeTab, closeOthers, closeAll, reorder]
  );

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}

// ---------------------------------------------------------------------------
// Per-screen refresh
// ---------------------------------------------------------------------------
// MainLayout wraps each pane in a TabRefreshProvider. A screen that wants to
// re-pull its data every time its tab is (re)activated calls:
//
//   useTabRefresh(() => loadRows());
//
// The callback does NOT fire on first mount (the screen's own effect already
// loaded then) — only on later activations.

const TabRefreshContext = createContext({ tick: 0, isActive: true });

export function TabRefreshProvider({ tick, isActive, children }) {
  const value = useMemo(() => ({ tick, isActive }), [tick, isActive]);
  return <TabRefreshContext.Provider value={value}>{children}</TabRefreshContext.Provider>;
}

export const useTabRefresh = (fn) => {
  const { tick } = useContext(TabRefreshContext);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    fnRef.current?.();
  }, [tick]);
};

// True while this screen is the visible tab (panes stay mounted when hidden).
export const useTabIsActive = () => useContext(TabRefreshContext).isActive;
